import React, { useState, useEffect } from 'react';
import {
  getReports, generateReport, getReportCategories, getSignedReportUrl, exportRawDatabase
} from '../services/adminService';
import {
  pageContainer, cardStyle, statsGrid,
  sectionTitle, thStyle, tdStyle, badgeStyle, tableSearch,
} from '../styles/theme';
import { useToast } from '../utils/useAdminPage';
import Paginator from '../components/Paginator';
import Toast from '../components/Toast';
import PageHeader from '../components/PageHeader';
import { useIsMobile } from '../utils/useIsMobile';

const ReportsCenter = () => {
  const isMobile = useIsMobile();
  const [categories, setCategories] = useState<any[]>([]);
  const [reports, setReports]       = useState<any[]>([]);
  const [loading, setLoading]       = useState(true);
  const [search, setSearch]         = useState('');
  const [page, setPage]             = useState(1);
  const pageSize = 10;

  const [formType, setFormType] = useState('REVENUE');
  const [formFormat, setFormFormat] = useState('PDF');
  const [startDate, setStartDate] = useState(new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0]);
  const [endDate, setEndDate] = useState(new Date().toISOString().split('T')[0]);
  const [generating, setGenerating] = useState(false);

  const [rawCollection, setRawCollection] = useState('USERS');
  const [exportingRaw, setExportingRaw] = useState(false);

  const { toast, showToast } = useToast();

  const handleRawExport = async () => {
    setExportingRaw(true);
    showToast(`Generating raw spreadsheet for ${rawCollection.toLowerCase()}...`, 'success');
    try {
      const blob = await exportRawDatabase(rawCollection);
      if (blob) {
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `${rawCollection.toLowerCase()}_export_${Date.now()}.csv`);
        document.body.appendChild(link);
        link.click();
        link.remove();
        showToast('Database export completed!', 'success');
      } else {
        showToast('Export file fetch failed', 'error');
      }
    } catch {
      showToast('Export failed', 'error');
    } finally {
      setExportingRaw(false);
    }
  };

  const fetchData = async () => {
    setLoading(true);
    const [reps, cats] = await Promise.all([getReports(), getReportCategories()]);
    setReports(reps || []);
    setCategories(cats || []);
    setLoading(false);
  };

  useEffect(() => { fetchData(); }, []);

  const handleGenerateCustom = async () => {
    if (!formType) {
      showToast('Please select a report type', 'error');
      return;
    }
    setGenerating(true);
    try {
      await generateReport({ 
        name: `${formType} Report`, 
        type: formType, 
        format: formFormat,
        filters: { startDate, endDate }
      });
      showToast('Report generated successfully!', 'success');
      fetchData();
    } catch {
      showToast('Failed to generate report', 'error');
    } finally {
      setGenerating(false);
    }
  };

  const handleDownload = async (id: string, name: string) => {
    showToast('Fetching secure document link...', 'success');
    const url = await getSignedReportUrl(id);
    if (url) {
      window.open(url, '_blank');
    } else {
      showToast('Failed to retrieve file url', 'error');
    }
  };

  // Filter reports by search
  const filtered = reports.filter(r =>
    !search || r.name?.toLowerCase().includes(search.toLowerCase()) || r.type?.toLowerCase().includes(search.toLowerCase())
  );
  const paged = filtered.slice((page - 1) * pageSize, page * pageSize);

  const catColors = ['#2563eb', '#10b981', '#8b5cf6', '#f97316', '#64748b', '#f59e0b'];
  const catIcons  = ['BA', 'SC', 'BK', 'FN', 'US', 'RP'];

  return (
    <div style={pageContainer}>
      <Toast toast={toast} />
      <PageHeader
        breadcrumb="Dashboard > Reports"
        title="Reports Center"
        subtitle="Generate, schedule, and export comprehensive business reports across all modules."
        onRefresh={fetchData}
      />

      {/* Category Cards */}
      <div style={{ ...statsGrid, gridTemplateColumns: isMobile ? "1fr" : "repeat(auto-fit, minmax(220px, 1fr))" }}>
        {(categories.length > 0 ? categories : [
          { label: 'Business Analytics', description: 'Revenue, growth, trends', count: 0, type: 'REVENUE' },
          { label: 'Scholar Performance', description: 'Ratings, earnings, activity', count: 0, type: 'SCHOLARS' },
          { label: 'Booking Reports', description: 'Completion, cancellation', count: 0, type: 'BOOKINGS' },
          { label: 'Financial Reports', description: 'Transactions, payouts', count: 0, type: 'PAYOUTS' },
          { label: 'Support Tickets', description: 'Response & resolution SLA', count: 0, type: 'TICKETS' },
          { label: 'Booking Disputes', description: 'Friction, resolution metrics', count: 0, type: 'DISPUTES' },
          { label: 'User Retention', description: 'Cohort retention rates', count: 0, type: 'RETENTION' },
        ]).map((cat: any, i: number) => (
          <div key={i} style={{
            ...cardStyle,
            background: catColors[i % catColors.length],
            color: '#fff',
            position: 'relative',
          }}>
            <div style={{
              position: 'absolute', top: 16, right: 16,
              background: 'rgba(255,255,255,0.2)', padding: '4px 10px',
              borderRadius: 8, fontSize: 12, fontWeight: 600,
            }}>
              {cat.count} Reports
            </div>
            <div style={{ fontSize: 28, marginBottom: 12 }}>{catIcons[i % catIcons.length]}</div>
            <div style={{ fontWeight: 700, fontSize: 16, marginBottom: 4 }}>{cat.label}</div>
            <div style={{ fontSize: 13, opacity: 0.85 }}>{cat.description}</div>
          </div>
        ))}
      </div>

      {/* Reports Table + Custom Builder */}
      <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 340px', gap: 32 }}>
        <div style={cardStyle}>
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, marginBottom: 20, flexWrap: 'wrap' }}>
            <input
              type="text" placeholder="Search reports…"
              style={{ ...tableSearch, flex: 1, minWidth: 180 }}
              value={search} onChange={e => { setSearch(e.target.value); setPage(1); }}
            />
          </div>

          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ textAlign: 'left', color: '#64748b', fontSize: 12, borderBottom: '1px solid #f1f5f9' }}>
                <th style={thStyle}>REPORT</th>
                <th style={thStyle}>TYPE</th>
                <th style={thStyle}>FORMAT</th>
                <th style={thStyle}>STATUS</th>
                <th style={{...thStyle, textAlign: 'right'}}>ACTIONS</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={5} style={{ padding: 40, textAlign: 'center', color: '#94a3b8' }}>Loading…</td></tr>
              ) : paged.length === 0 ? (
                <tr><td colSpan={5} style={{ padding: 40, textAlign: 'center', color: '#94a3b8' }}>No reports found.</td></tr>
              ) : paged.map((r: any) => (
                <tr key={r.id} style={{ borderBottom: '1px solid #f1f5f9' }}>
                  <td style={tdStyle}>
                    <div style={{ fontWeight: 700, fontSize: 14 }}>{r.name}</div>
                    <div style={{ fontSize: 11, color: '#94a3b8' }}>{new Date(r.createdAt).toLocaleDateString()}</div>
                  </td>
                  <td style={tdStyle}><span style={badgeStyle}>{r.type}</span></td>
                  <td style={tdStyle}><span style={{ ...badgeStyle, background: '#f5f3ff', color: '#7c3aed' }}>{r.format || 'PDF'}</span></td>
                  <td style={tdStyle}>
                    <span style={{
                      padding: '4px 10px', borderRadius: 20, fontSize: 11, fontWeight: 700,
                      background: r.status === 'COMPLETED' ? '#f0fdf4' : '#fffbeb',
                      color: r.status === 'COMPLETED' ? '#15803d' : '#b45309',
                    }}>
                      {r.status}
                    </span>
                  </td>
                  <td style={{...tdStyle, textAlign: 'right'}}>
                    {r.status === 'COMPLETED' ? (
                        <button
                          onClick={() => handleDownload(r.id, r.name)}
                          style={{
                              padding: '6px 12px', background: '#e0e7ff', color: '#4338ca',
                              border: 'none', borderRadius: 6, fontWeight: 600, fontSize: 12,
                              cursor: 'pointer'
                          }}
                        >
                          View Document
                        </button>
                    ) : (
                        <span style={{ fontSize: 12, color: '#94a3b8' }}>Processing</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          <Paginator total={filtered.length} page={page} pageSize={pageSize} onPage={setPage} />
        </div>

        {/* Custom Report Builder */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
          <div style={cardStyle}>
            <h3 style={{ ...sectionTitle, marginBottom: 8 }}>Generate Report</h3>
            <p style={{ color: '#64748b', fontSize: 14, marginBottom: 20 }}>
              Create tailored reports with custom filters.
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16, marginBottom: 20 }}>
              <div>
                <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#475569', marginBottom: 8 }}>Report Type</label>
                <select 
                   value={formType} 
                   onChange={(e) => setFormType(e.target.value)}
                   style={{ width: '100%', padding: 10, borderRadius: 8, border: '1px solid #e2e8f0', fontSize: 14 }}
                >
                  <option value="REVENUE">Revenue</option>
                  <option value="SCHOLARS">Scholars</option>
                  <option value="BOOKINGS">Bookings</option>
                  <option value="PAYOUTS">Payouts</option>
                  <option value="TICKETS">Support Tickets</option>
                  <option value="DISPUTES">Booking Disputes</option>
                  <option value="RETENTION">User Retention</option>
                  <option value="CUSTOM">Custom</option>
                </select>
              </div>
              <div>
                <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#475569', marginBottom: 8 }}>Export Format</label>
                <select 
                   value={formFormat}
                   onChange={(e) => setFormFormat(e.target.value)}
                   style={{ width: '100%', padding: 10, borderRadius: 8, border: '1px solid #e2e8f0', fontSize: 14 }}
                >
                  <option value="PDF">PDF Document</option>
                  <option value="CSV">CSV Spreadsheet</option>
                </select>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <div>
                  <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#475569', marginBottom: 8 }}>Start Date</label>
                  <input 
                    type="date" 
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    style={{ width: '100%', padding: 10, borderRadius: 8, border: '1px solid #e2e8f0', fontSize: 14 }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#475569', marginBottom: 8 }}>End Date</label>
                  <input 
                    type="date" 
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    style={{ width: '100%', padding: 10, borderRadius: 8, border: '1px solid #e2e8f0', fontSize: 14 }}
                  />
                </div>
              </div>
            </div>
            <button
              onClick={handleGenerateCustom}
              disabled={generating}
              style={{
                width: '100%', padding: 14, background: generating ? '#94a3b8' : '#2563eb', color: '#fff',
                border: 'none', borderRadius: 10, fontWeight: 700, cursor: generating ? 'not-allowed' : 'pointer',
              }}
            >
              Generate Report
            </button>
          </div>

          {/* Database Raw Export */}
          <div style={cardStyle}>
            <h3 style={{ ...sectionTitle, marginBottom: 8 }}>Database Raw Export</h3>
            <p style={{ color: '#64748b', fontSize: 14, marginBottom: 20 }}>
              Instantly export complete raw tables from the database as tabular CSV spreadsheets.
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16, marginBottom: 20 }}>
              <div>
                <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#475569', marginBottom: 8 }}>Target Collection</label>
                <select 
                   value={rawCollection} 
                   onChange={(e) => setRawCollection(e.target.value)}
                   style={{ width: '100%', padding: 10, borderRadius: 8, border: '1px solid #e2e8f0', fontSize: 14 }}
                >
                  <option value="USERS">Users Directory</option>
                  <option value="SCHOLARS">Scholars Registry</option>
                  <option value="BOOKINGS">Bookings Log</option>
                  <option value="PAYMENTS">Payments & Transactions</option>
                  <option value="SUPPORT_TICKETS">Support Tickets</option>
                  <option value="DISPUTES">Booking Disputes</option>
                  <option value="PAYOUTS">Scholar Payouts</option>
                </select>
              </div>
            </div>
            <button
              onClick={handleRawExport}
              disabled={exportingRaw}
              style={{
                width: '100%', padding: 14, background: exportingRaw ? '#94a3b8' : '#10b981', color: '#fff',
                border: 'none', borderRadius: 10, fontWeight: 700, cursor: exportingRaw ? 'not-allowed' : 'pointer',
              }}
            >
              {exportingRaw ? 'Exporting…' : 'Export to CSV'}
            </button>
          </div>

          {/* Scheduled Reports */}
          <div style={cardStyle}>
            <h3 style={sectionTitle}>Scheduled Reports</h3>
            {reports.filter(r => r.schedule && r.schedule !== 'NONE').length > 0 ? (
              reports.filter(r => r.schedule && r.schedule !== 'NONE').map((r: any) => (
                <div key={r.id} style={{ padding: '12px 0', borderBottom: '1px solid #f1f5f9' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                    <span style={{ fontWeight: 600, fontSize: 14 }}>{r.name}</span>
                    <span style={{
                      fontSize: 10, fontWeight: 800, padding: '2px 6px', borderRadius: 4,
                      background: r.status === 'ACTIVE' ? '#f0fdf4' : '#f1f5f9',
                      color: r.status === 'ACTIVE' ? '#15803d' : '#94a3b8',
                    }}>
                      {r.status}
                    </span>
                  </div>
                  <div style={{ fontSize: 12, color: '#94a3b8' }}>{r.schedule}</div>
                </div>
              ))
            ) : (
              <p style={{ fontSize: 12, color: '#94a3b8' }}>No scheduled reports</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReportsCenter;
